import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { IMidtermExamPaymentCourse, IMidtermExamPaymentPayload, IMidtermExamPaymentTeacher } from '../../models/MidtermExamPayment';

import { AppService } from '../../services/app.service';
import { MidtermExamPaymentService } from '../../services/midterm-exam-payment.service';
import { IPersonSearchResultItem } from '../../models/Person';

const cache: {
    courses: { [upn: string]: IMidtermExamPaymentCourse[] },
    teachers: { [course: string]: IMidtermExamPaymentTeacher[] }
} = {
    courses: {},
    teachers: {}
}

let defaultValues: IDefaultValues;

export interface IDefaultValues {
    courseId?: string;
    teacherId?: string;
    reason?: string;
    studentUpn?: string;
    studentFullName?: string;
}

export function setNextPaymentValues(data: IDefaultValues) {
    defaultValues = data;
}

@Component({
    selector: 'app-midterm-exam-payment-add',
    templateUrl: './add.component.html'
})
export class MidtermExamPaymentAddComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: MidtermExamPaymentService
    ) { }

    courses: IMidtermExamPaymentCourse[] = [];
    teachers: IMidtermExamPaymentTeacher[] = [];

    course: IMidtermExamPaymentCourse;
    teacher: IMidtermExamPaymentTeacher;
    reason: string;
    amount: number;

    studentPickerOpened: boolean;
    canPickStudent: boolean;

    readonly courseDisplayFn = (option: IMidtermExamPaymentCourse) => `${option.Name} (${option.Code}) - ${option.Semester}`;
    readonly teacherDisplayFn = (option: IMidtermExamPaymentTeacher) => `${option.Name} ${option.Surname}`;
    readonly reasonMaxLength = 1000;

    get studentDisplay() {
        return this.student
            ? `${this.student.name} (${this.student.upn})`
            : `${this.app.currentUser.firstName} ${this.app.currentUser.lastName} (${this.app.currentUser.username})`;
    }

    private student: { name: string, upn: string };
    private defaultCourseId: string;
    private defaultTeacherId: string;

    @ViewChild('paymentFormContainer', { static: true }) paymentFormContainer: ElementRef;

    ngOnInit() {
        if (defaultValues) {
            this.reason = defaultValues.reason || '';
            this.defaultCourseId = defaultValues.courseId;
            this.defaultTeacherId = defaultValues.teacherId;

            if (defaultValues.studentUpn && defaultValues.studentFullName) {
                this.student = {
                    name: defaultValues.studentFullName,
                    upn: defaultValues.studentUpn
                };
            }

            defaultValues = undefined;
        }

        this.canPickStudent = this.app.currentUser.hasRight('MIDTERM_EXAM_PAYMENT.SET_STUDENT');

        if (this.canPickStudent && !this.student) {
            this.studentPickerOpened = true;
        } else {
            this.loadCourses();
        }
    }

    pickStudent(student: IPersonSearchResultItem) {
        this.student = {
            name: `${student.FirstName} ${student.LastName}`,
            upn: student.Upn
        };
        this.course = undefined;
        this.teacher = undefined;
        this.studentPickerOpened = false;

        this.loadCourses();
    }

    onCourseChange(event: IMidtermExamPaymentCourse) {
        this.teacher = undefined;
        this.amount = event.PaymentAmount;

        this.loadTeachers();
    }

    getMaxLengthValidationPattern(value: string, max: number): string {
        return (!value || (value.length <= max)) ? '' : '$^';
    }

    submit() {
        const payload: IMidtermExamPaymentPayload = {
            courseId: this.course.Id,
            reason: this.reason,
            teacherId: this.teacher.Id
        };

        if (this.student) {
            payload.studentUpn = this.student.upn;
            payload.studentFullName = this.student.name;
        }

        this.app.addLoading(this.service.add(payload)).subscribe(result => {
            this.submitPayment(result.Url, result.Data);

            this.app.confirm({
                text: this.app.translate('midtermExamPayment_inProgressMessage'),
                title: this.app.translate('midtermExamPayment_inProgressTitle'),
                cancelText: ''
            }, () => {
                this.app.navigateByUrl('/midterm-exam-payments');
            });
        });
    }

    private loadCourses() {
        const upn = this.student?.upn || this.app.currentUser.username;

        const done = (data: IMidtermExamPaymentCourse[]) => {
            this.courses = data;

            if (this.defaultCourseId) {
                this.course = data.find(c => c.Id === this.defaultCourseId);
                this.defaultCourseId = undefined;
            } else if (data.length == 1) {
                this.course = data[0];
            }

            if (this.course) {
                this.loadTeachers();
            }
        }

        if (cache.courses[upn]) {
            done(cache.courses[upn])
        } else {
            this.app.addLoading(this.service.getCourses(upn)).subscribe(data => {
                cache.courses[upn] = data;
                done(data);
            });
        }
    }

    private loadTeachers() {
        const code = this.course.Code;

        const done = (data: IMidtermExamPaymentTeacher[]) => {
            this.teachers = data;

            if (this.defaultTeacherId) {
                this.teacher = data.find(t => t.Id === this.defaultTeacherId);
                this.defaultTeacherId = undefined;
            } else if (data.length == 1) {
                this.teacher = data[0];
            }
        }

        if (cache.teachers[code]) {
            done(cache.teachers[code])
        } else {
            this.app.addLoading(this.service.getTeachers(code, this.student?.upn)).subscribe(data => {
                cache.teachers[code] = data;
                done(data);
            });
        }
    }

    private submitPayment(url: string, data: string) {
        const pf = document.createElement('form');
        pf.method = 'post';
        pf.target = '_blank';
        pf.action = url;

        const pi = document.createElement('input');
        pi.name = 'payment_data';
        pi.value = data;

        pf.appendChild(pi);
        this.paymentFormContainer.nativeElement.appendChild(pf);

        pf.submit();
    }
}
